import { useEffect, useRef, useState, useCallback } from "react";
import { IMG } from "@/lib/images";
import { useSound } from "@/lib/SoundContext";

const rand = (a, b) => Math.random() * (b - a) + a;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const REACTIONS = ["🐾", "♡", "✦"];

// Pose + micro-animation for each behavioural state.
const POSE = {
  enter:  { img: "catWalking", anim: "dl-anim-walk",   moving: true },
  walk:   { img: "catWalking", anim: "dl-anim-walk",   moving: true },
  run:    { img: "catWalking", anim: "dl-anim-run",    moving: true },
  leave:  { img: "catWalking", anim: "dl-anim-walk",   moving: true },
  stop:   { img: "catWalking", anim: "",               moving: false },
  sit:    { img: "catSitting", anim: "dl-anim-sit",    moving: false },
  look:   { img: "catSitting", anim: "dl-anim-look",   moving: false },
  groom:  { img: "catSitting", anim: "dl-anim-groom",  moving: false },
  yawn:   { img: "catSitting", anim: "dl-anim-yawn",   moving: false },
  stretch:{ img: "catStretch", anim: "dl-anim-stretch",moving: false },
  lie:    { img: "catLying",   anim: "dl-anim-lie",    moving: false },
  sleep:  { img: "catSleeping",anim: "dl-anim-sleep",  moving: false },
};

// A grey Maine Coon that genuinely lives here: a semi-random behaviour engine
// chains many distinct states (walk, run, sit, groom, stretch, yawn, lie, sleep…).
export default function CompanionCat() {
  const { startPurr, stopPurr } = useSound();
  const [st, setSt] = useState({ state: "sleep", x: -20, dir: 1, w: 145, visible: false });
  const [react, setReact] = useState(null);
  const [speech, setSpeech] = useState(null);
  const speechT = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const actions = useRef(0);

  const schedule = useCallback((fn, ms) => { timer.current = setTimeout(fn, ms); }, []);

  const setTransition = (ms) => {
    const el = document.getElementById("dl-cat-wrap");
    if (el) el.style.transition = ms ? `left ${ms}ms linear, opacity .9s ease` : "opacity .9s ease";
  };

  const go = useCallback(function go(state) {
    if (!alive.current) return;
    const conf = POSE[state];
    actions.current += 1;

    if (state === "enter") {
      const fromLeft = Math.random() > 0.5;
      const startX = fromLeft ? -18 : 100;
      const target = rand(12, 58);
      const dir = target > startX ? 1 : -1;
      const dist = Math.abs(target - startX);
      const dur = (dist / rand(7, 10)) * 1000;
      setSt({ state, x: startX, dir, w: rand(120, 165), visible: true });
      setTransition(0);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        setTransition(dur);
        setSt((s) => ({ ...s, x: target }));
      }));
      schedule(() => go(pick(["sit", "sit", "look", "stretch", "groom", "lie"])), dur + 120);
      return;
    }

    if (state === "walk" || state === "run") {
      const speed = state === "run" ? rand(20, 26) : rand(7, 10);
      const target = rand(8, 60);
      setSt((s) => {
        const dir = target > s.x ? 1 : -1;
        const dur = (Math.abs(target - s.x) / speed) * 1000;
        setTransition(0);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          setTransition(dur);
          setSt((s2) => ({ ...s2, x: target }));
        }));
        schedule(nextIdle, dur + 120);
        return { ...s, state, dir };
      });
      return;
    }

    if (state === "leave") {
      setSt((s) => {
        const leaveRight = s.x > 50;
        const target = leaveRight ? 122 : -22;
        const dur = (Math.abs(target - s.x) / rand(8, 12)) * 1000;
        setTransition(0);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          setTransition(dur);
          setSt((s2) => ({ ...s2, x: target }));
        }));
        schedule(() => {
          stopPurr();
          setSt((s3) => ({ ...s3, visible: false }));
          actions.current = 0;
          schedule(() => go("enter"), rand(30000, 70000)); // comes back later
        }, dur + 200);
        return { ...s, state, dir: leaveRight ? 1 : -1 };
      });
      return;
    }

    // Idle states: hold, then move on. Occasionally flip to "look" at the user.
    const dur = {
      stop: rand(700, 1500), sit: rand(3000, 7000), look: rand(2500, 5000),
      groom: rand(2600, 4600), yawn: 1100, stretch: 1400,
      lie: rand(6000, 12000), sleep: rand(9000, 18000),
    }[state] || 3000;
    setSt((s) => ({ ...s, state, dir: state === "look" && Math.random() > 0.5 ? -s.dir : s.dir }));
    schedule(nextIdle, dur);
  }, [schedule, startPurr, stopPurr]);

  const nextIdle = useCallback(() => {
    if (!alive.current) return;
    if (actions.current > rand(6, 10)) { go("leave"); return; }
    // weighted next behaviour
    const bag = [
      "sit", "sit", "look", "look", "groom", "groom", "stretch", "yawn",
      "lie", "lie", "sleep", "walk", "walk", "walk", "run", "stop",
    ];
    go(pick(bag));
  }, [go]);

  useEffect(() => {
    alive.current = true;
    schedule(() => go("enter"), rand(4000, 8000));
    return () => { alive.current = false; clearTimeout(timer.current); stopPurr(); };
  }, [go, schedule, stopPurr]);

  // Échange avec Dorothée : elle l'appelle, il répond et se pose près d'elle.
  useEffect(() => {
    const onSay = (e) => {
      setSpeech(e.detail?.text || null);
      clearTimeout(speechT.current);
      speechT.current = setTimeout(() => setSpeech(null), e.detail?.ms || 2400);
    };
    const onEngage = (e) => {
      if (!alive.current) return;
      clearTimeout(timer.current);
      setSt((s) => ({ ...s, state: "look" }));
      schedule(nextIdle, (e.detail?.duration || 9000) + 800);
    };
    window.addEventListener("dl-cat-say", onSay);
    window.addEventListener("dl-cat-engage", onEngage);
    return () => {
      window.removeEventListener("dl-cat-say", onSay);
      window.removeEventListener("dl-cat-engage", onEngage);
      clearTimeout(speechT.current);
    };
  }, [schedule, nextIdle, startPurr]);

  const onPet = () => {
    startPurr();
    setTimeout(stopPurr, 1300);
    clearTimeout(timer.current);
    setSt((s) => ({ ...s, state: "look" }));
    setReact(pick(REACTIONS) + "|" + Date.now());
    schedule(() => setReact(null), 1100);
    schedule(nextIdle, 2600);
  };

  const conf = POSE[st.state];
  const src = IMG[conf.img];

  return (
    <div id="dl-cat-wrap" className="dl-cat-wrap" data-testid="companion-cat"
style={{ left: `${st.x}vw`, width: st.w, opacity: st.visible ? 1 : 0, pointerEvents: "none" }}>
      
      <div className="relative" style={{ cursor: "pointer" }} onClick={onPet} role="img" aria-label="Le chat de Dorothée">
        {speech && (
          <span data-testid="cat-speech" className="absolute left-1/2 -top-10 -translate-x-1/2 whitespace-nowrap bg-[#050814]/92 border border-[rgba(198,167,106,0.45)] px-3 py-1.5 rounded-2xl text-[#F3E9D2] text-sm serif italic pointer-events-none" style={{ boxShadow: "0 6px 20px rgba(0,0,0,0.5)" }}>
            {speech}
          </span>
        )}
        {react && (
          <span key={react} className="dl-cat-react absolute left-1/2 -top-4 -translate-x-1/2 text-[#D4B978] text-lg pointer-events-none">
            {react.split("|")[0]}
          </span>
        )}
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 62%, rgba(212,185,120,0.18), transparent 70%)", filter: "blur(16px)" }} />
        <img
          src={src} alt="" draggable="false"
          className={`${conf.anim} relative w-full select-none`}
          style={{
            transform: `scaleX(${st.dir})`,
            filter: "drop-shadow(0 12px 20px rgba(0,0,0,0.6))",
            maskImage: "radial-gradient(ellipse at 50% 55%, black 47%, transparent 78%)",
            WebkitMaskImage: "radial-gradient(ellipse at 50% 55%, black 47%, transparent 78%)",
          }}
        />
      </div>
    </div>
  );
}
